/**
 * Phase 8 — pure Kronos timesheet helpers.
 *
 * Week math + status-transition validation + grouping utilities for
 * the weekly view. Kept dependency-free so the Gen2 Firestore trigger
 * in `services/functions/src/on-timesheet-status-change.ts` can reuse
 * the same week-range / stamping logic without pulling React or the
 * client Firestore SDK.
 *
 * The server rule in `firestore.rules` is the authoritative gate; the
 * helpers here **mirror** that gate so the UI can disable actions
 * instead of letting Firestore reject them with permission_denied.
 */

import type { TimeEntry } from '@altyor-kronos/shared-types';
import { startOfWeekMonday } from './dashboardAggregations';

// ─── Week range ────────────────────────────────────────────────────

export interface WeekRange {
  /** Monday 00:00:00.000 local. */
  start: Date;
  /** Sunday 23:59:59.999 local — inclusive upper bound. */
  end: Date;
}

/**
 * Compute the ISO week range (Monday 00:00 → Sunday 23:59:59.999) for
 * any anchor date. Sunday resolves backwards to the previous Monday
 * (ISO semantics, which match Altyor's French workweek convention).
 *
 * End uses `.999` so Firestore range queries of the form
 * `startTime <= weekEnd` include an entry logged on Sunday evening.
 */
export function getWeekRange(anchor: Date): WeekRange {
  const start = startOfWeekMonday(anchor);
  const end = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 6,
    23,
    59,
    59,
    999,
  );
  return { start, end };
}

/**
 * Stable timesheet doc id: `{userId}_{YYYY-MM-DD of the week's Monday}`.
 * The UI can `getDoc(doc('timesheets', timesheetIdForWeek(uid, anchor)))`
 * without a prior query, and the same id is used by the Cloud Function.
 */
export function timesheetIdForWeek(userId: string, anchor: Date): string {
  const mon = startOfWeekMonday(anchor);
  const y = mon.getFullYear();
  const m = String(mon.getMonth() + 1).padStart(2, '0');
  const d = String(mon.getDate()).padStart(2, '0');
  return `${userId}_${y}-${m}-${d}`;
}

// ─── Aggregation helpers ───────────────────────────────────────────

/**
 * Sum all entry durations and return hours rounded to 2 decimals.
 * Rounding is performed up-front so the value stored in Firestore is
 * stable and diff-friendly in UIs that echo it back.
 */
export function computeTotalHours(entries: readonly TimeEntry[]): number {
  const seconds = entries.reduce((acc, e) => acc + (e.durationSeconds ?? 0), 0);
  const hours = seconds / 3600;
  return Math.round(hours * 100) / 100;
}

/** Inclusive on both ends — see `getWeekRange` for the .999 boundary. */
export function isEntryInWeek(entry: TimeEntry, range: WeekRange): boolean {
  const ms = entry.startTime.toMillis();
  return ms >= range.start.getTime() && ms <= range.end.getTime();
}

export interface DayBucket {
  /** Local 00:00 of the day. */
  day: Date;
  entries: TimeEntry[];
  totalSeconds: number;
}

/**
 * Group entries into 7 Monday-first day buckets covering `range`.
 * Entries outside the range are silently dropped — callers should
 * already have filtered by Firestore query, this is defensive.
 *
 * Every bucket is returned even if empty so a weekly view can render
 * all 7 columns without having to reason about gaps.
 */
export function entriesGroupedByDay(
  entries: readonly TimeEntry[],
  range: WeekRange,
): DayBucket[] {
  const buckets: DayBucket[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(
      range.start.getFullYear(),
      range.start.getMonth(),
      range.start.getDate() + i,
      0,
      0,
      0,
      0,
    );
    buckets.push({ day, entries: [], totalSeconds: 0 });
  }

  for (const e of entries) {
    if (!isEntryInWeek(e, range)) continue;
    const ms = e.startTime.toMillis();
    const dayIndex = Math.floor((ms - range.start.getTime()) / 86400000);
    // Guard against a floating-point edge where an entry on the exact
    // start of day 7 could land at index 7 — clamp into [0,6].
    const idx = Math.max(0, Math.min(6, dayIndex));
    buckets[idx].entries.push(e);
    buckets[idx].totalSeconds += e.durationSeconds ?? 0;
  }

  return buckets;
}


/** One entry of a timesheet cell (project × designation × day). */
export interface CellEntry {
  id: string;
  startMs: number;
  durationSeconds: number;
}

export type CellOp =
  | { kind: 'create'; start: Date; end: Date; durationSeconds: number }
  | { kind: 'update'; id: string; durationSeconds: number; endTime: Date }
  | { kind: 'delete'; id: string };

/**
 * Writes that bring a cell from its current total to `newSeconds` (#66).
 *
 * - every endTime is `startTime + duration`, never derived from the day;
 * - growing extends the latest entry;
 * - shrinking eats entries from the latest backwards, deleting the ones it
 *   empties, so the cell total always lands exactly on `newSeconds`.
 *
 * Deletes come out as ops so the caller can ask for confirmation first.
 */
export function planCellChange(
  existing: readonly CellEntry[],
  newSeconds: number,
  dayStart: Date,
): CellOp[] {
  const total = existing.reduce((s, e) => s + e.durationSeconds, 0);
  if (newSeconds === total) return [];
  const resized = (e: CellEntry, durationSeconds: number): CellOp => ({
    kind: 'update',
    id: e.id,
    durationSeconds,
    endTime: new Date(e.startMs + durationSeconds * 1000),
  });

  if (existing.length === 0) {
    return [{
      kind: 'create',
      start: dayStart,
      end: new Date(dayStart.getTime() + newSeconds * 1000),
      durationSeconds: newSeconds,
    }];
  }

  const latestFirst = [...existing].sort((a, b) => b.startMs - a.startMs);
  if (newSeconds > total) {
    const last = latestFirst[0];
    return [resized(last, last.durationSeconds + newSeconds - total)];
  }

  const ops: CellOp[] = [];
  let toRemove = total - newSeconds;
  for (const e of latestFirst) {
    if (toRemove <= 0) break;
    if (e.durationSeconds <= toRemove) {
      ops.push({ kind: 'delete', id: e.id });
      toRemove -= e.durationSeconds;
    } else {
      ops.push(resized(e, e.durationSeconds - toRemove));
      toRemove = 0;
    }
  }
  return ops;
}
